/**
 * pendency — predicates for "this item is past its due date".
 *
 * Used by the `.pd-pendency-pulse` class to surface a slow crimson
 * breathe on rows that need attention. Each predicate is intentionally
 * narrow so that "overdue" matches the user's mental model per entity:
 *
 *   • Task    → has a dueDate, the date is past, not COMPLETED/ARCHIVED
 *   • Payment → consumes the server's IST-computed status === 'OVERDUE'
 *              (no client date math — see backend utils/datetime_ist.py)
 *   • Call    → status not COMPLETED, scheduledTime in the past
 *   • Decision (Today queue) → routes to the per-type predicate above
 */
import { todayIST } from './locale';


const todayISO = () => todayIST(); // tz-correct "today" (DATETIME-IST-TODAY-SWEEP-1)

const isPastISODate = (dateStr) => {
  if (!dateStr) return false;
  // Compare YYYY-MM-DD strings lexicographically — works for ISO dates
  // and avoids timezone juggling for date-only fields.
  return String(dateStr).slice(0, 10) < todayISO();
};

const isPastDateTime = (iso) => {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) && t < Date.now();
};

export function isTaskOverdue(task) {
  if (!task) return false;
  if (task.status === 'COMPLETED' || task.status === 'ARCHIVED') return false;
  return isPastISODate(task.dueDate);
}

export function isPaymentOverdue(payment) {
  // Server is the single source of truth for IST overdue (see datetime_ist.py).
  // No client date math.
  return payment?.status === 'OVERDUE';
}

export function isCallOverdue(call) {
  if (!call) return false;
  if (call.status === 'COMPLETED') return false;
  return isPastDateTime(call.scheduledTime);
}

/**
 * Single dispatch for the Today decision queue, which mixes task /
 * call / payment / reading rows behind a unified `decision` shape.
 * Returns true only for entity types we want pulsing.
 */
export function isDecisionOverdue(decision) {
  if (!decision) return false;
  // Don't pulse completed or graveyard rows — they're done in their own way.
  if (decision.category === 'COMPLETED' || decision.category === 'GRAVEYARD') return false;
  switch (decision.type) {
    case 'task':    return isPastISODate(decision.dueDate);
    case 'payment': return decision.isOverdue === true || decision.paymentStatus === 'OVERDUE';
    case 'call':    return isPastDateTime(decision.scheduledTime);
    default:        return false;
  }
}
